import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Form,
  LookupMultiSelect,
  Text
} from 'onefaceui-react-components';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import { getResourceTo } from 'react/utils/utils';
import { actionCreators as actionsModals } from 'react/api/actions/modals';
import { actionCreators as toastActions } from 'react/api/actions/toastAlerts';

import Modal from 'react/components/Modal';

import { getLookupExtraParams } from 'react/utils/lookupUtils';
import { useRouteAssignments } from 'react/queries/RouteAssignments';
import queryClient from 'react/services/queryClient';
import Loading from 'react/components/Loading';
import CustomSelectedItem from './CustomSelectedItem';

import {
  Container,
  ModalFooter,
  LoadingContainer
} from './RouteAssignments.styles';

const tabIndexMapper = {
  DRIVER: 0,
  HELPER: 1,
  EQUIPMENT: 2,
  TAG: 3
};

function RouteAssignmentsModal() {
  const formRefs = useRef([]);
  const dispatch = useDispatch();
  const [activeIndexTab, setActiveIndexTab] = useState(tabIndexMapper.DRIVER);
  const [selectedOption, setSelectedOption] = useState(null);

  const MODAL_KEY = 'routeAssignments';

  const modelMapper = {
    driver: new App.Models.RouteDriverAssignment(),
    helper: new App.Models.RouteHelperAssignment(),
    equipment: new App.Models.RouteEquipmentAssignment(),
    tag: new App.Models.RouteTagAssignment()
  };

  const {
    title,
    isOpen,
    data: { routeId, subtitle }
  } = useSelector((state) => state.modals.get(MODAL_KEY).toJS());

  const {
    data: route = {},
    isFetching,
    refetch
  } = useRouteAssignments(routeId);

  const [isUpdating, setIsUpdating] = useState(false);

  const allAssignments = useMemo(() => {
    const driverAssignments = route?.driverAssignments || [];
    const helperAssignments = route?.helperAssignments || [];
    const equipmentAssignments = route?.equipmentAssignments || [];
    const tagAssignments = route?.tagAssignments || [];

    return {
      driver: {
        assignments: driverAssignments,
        list: driverAssignments.map(({ driver }) => driver)
      },
      helper: {
        assignments: helperAssignments,
        list: helperAssignments.map(({ helper }) => helper)
      },
      equipment: {
        assignments: equipmentAssignments,
        list: equipmentAssignments.map(({ equipment, principal }) => ({
          ...equipment,
          principal
        }))
      },
      tag: {
        assignments: tagAssignments,
        list: tagAssignments.map(({ tag }) => tag)
      }
    };
  }, [route]);

  const closeModal = useCallback(() => {
    dispatch(
      actionsModals.closeModal({
        modalKey: MODAL_KEY
      })
    );
  }, []);

  const showToastError = useCallback(
    (data) => {
      const { errorMessages } = data?.responseText
        ? JSON.parse(data.responseText)
        : [];

      const errorMessageDefault = 'eventDetails.internal.error';

      const errorMessage =
        errorMessages[0]?.resource?.key || errorMessageDefault;

      setIsUpdating(false);
      dispatch(
        toastActions.addToast({
          id: Date.now(),
          type: 'error',
          data: {
            title: getResourceTo('routeAssignments.error.title'),
            description: getResourceTo(errorMessage)
          }
        })
      );
    },
    [dispatch]
  );

  const showToastSuccess = useCallback(
    (type) => {
      setIsUpdating(false);

      const successDescriptionMapper = {
        driver: {
          title: 'RouteAssignments.AssignDriver.success.title',
          description: 'RouteAssignments.AssignDriver.success.description'
        },
        equipment: {
          title: 'RouteAssignments.AssignEquipment.success.title',
          description: 'RouteAssignments.AssignEquipment.success.description'
        },
        helper: {
          title: 'RouteAssignments.AssignHelper.success.title',
          description: 'RouteAssignments.AssignHelper.success.description'
        },
        tag: {
          title: 'RouteAssignments.AssignTag.success.title',
          description: 'RouteAssignments.AssignTag.success.description'
        }
      };

      dispatch(
        toastActions.addToast({
          id: Date.now(),
          type: 'success',
          data: {
            title: getResourceTo(successDescriptionMapper[type].title),
            description: getResourceTo(successDescriptionMapper[type].description)
          }
        })
      );
      setIsUpdating(false);
      queryClient.invalidateQueries(['routeAssignments', routeId]);
      refetch();
    },
    [dispatch]
  );

  const showToastInfo = useCallback((type) => {
    const infoDescriptionMapper = {
      driver: {
        title: 'RouteAssignments.AssignDriver.info.title',
        description: 'RouteAssignments.AssignDriver.info.description'
      },
      equipment: {
        title: 'RouteAssignments.AssignEquipment.info.title',
        description: 'RouteAssignments.AssignEquipment.info.description'
      },
      helper: {
        title: 'RouteAssignments.AssignHelper.info.title',
        description: 'RouteAssignments.AssignHelper.info.description'
      },
      tag: {
        title: 'RouteAssignments.AssignTag.info.title',
        description: 'RouteAssignments.AssignTag.info.description'
      }
    };

    dispatch(
      toastActions.addToast({
        id: Date.now(),
        type: 'info',
        data: {
          title: getResourceTo(infoDescriptionMapper[type].title),
          description: getResourceTo(infoDescriptionMapper[type].description)
        }
      })
    );
  }, [dispatch]);

  const handleSubmit = useCallback(
    (data, type) => {
      let listData = data[type];
      const listDataIDs = listData.map((item) => item.id);

      const model = modelMapper[type];

      setIsUpdating(true);

      let { assignments, list } = allAssignments[type];

      if (assignments.length && !listData.length) {
        const ids = assignments.map((entity) => entity.id);

        model.unassignAllToRoute(
          { assignmentsListId: ids },
          () => showToastSuccess(type),
          showToastError,
          false
        );

        return;
      }

      if (type === 'equipment') {
        listData = listData.map((assignment) => ({
          ...assignment,
          principal: assignment.id === selectedOption
        }));
      }

      const orderListItems = (items) =>
        items
          .map(({ id, principal }) => ({ id, principal }))
          .sort((a, b) => a.id - b.id);

      const hasListDiff = (list1, list2) =>
        JSON.stringify(list1) !== JSON.stringify(list2);

      const equipmentHasChanged =
        type === 'equipment' &&
        hasListDiff(orderListItems(list), orderListItems(listData));

      const toAssign = listData
        .filter(
          (item) =>
            !list.some((assignment) => assignment.id === item.id) ||
            equipmentHasChanged
        )
        .map((entity) => ({
          route: { id: routeId },
          [type]: { id: entity.id },
          ...(type === 'equipment' && { principal: entity.principal })
        }));

      const toUnassign = assignments
        .filter(
          (assignment) =>
            !listDataIDs.includes(assignment[type].id) || equipmentHasChanged
        )
        .map((assignment) => assignment.id);

      if (!toAssign.length && !toUnassign.length) {
        showToastInfo(type);
        setIsUpdating(false);
        return;
      }

      model.unassignAllToRoute(
        { assignmentsListId: toUnassign },
        () => {
          if (toAssign.length) {
            model.assignToRouteInBatch(
              { list: toAssign },
              () => showToastSuccess(type),
              showToastError,
              false
            );
            return;
          }

          showToastSuccess(type);
        },
        showToastError,
        false
      );
    },
    [
      allAssignments,
      showToastSuccess,
      showToastError,
      showToastInfo,
      selectedOption
    ]
  );

  const tabsContentList = useMemo(() => {
    return [
      {
        type: 'driver',
        tabIndex: tabIndexMapper.DRIVER,
        tabTitle: getResourceTo('Route.AssingDriver.label.driver'),
        testid: 'routeAssignments-drivers',
        title: getResourceTo('driverAssignments'),
        lookupLabel: getResourceTo('PhotoAlbum.filter.driver'),
        lookupParams: {
          entity: 'Driver',
          defaultField: 'name',
          dataTestId: 'driver-field'
        }
      },
      {
        type: 'helper',
        tabIndex: tabIndexMapper.HELPER,
        tabTitle: getResourceTo('Route.AssingHelper.label.driver'),
        testid: 'routeAssignments-helpers',
        title: getResourceTo('helperAssignments'),
        lookupLabel: getResourceTo('Route.AssingHelper.label.driver'),
        lookupParams: {
          entity: 'Driver',
          defaultField: 'name',
          dataTestId: 'helpers-field',
          customPayload: { driverType: 'HELPER' }
        }
      },
      {
        type: 'equipment',
        tabIndex: tabIndexMapper.EQUIPMENT,
        tabTitle: getResourceTo('Route.AssingEquipment.label.equipment'),
        testid: 'routeAssignments-equipments',
        title: getResourceTo('equipmentAssignments'),
        lookupLabel: getResourceTo('Route.AssingEquipment.label.equipment'),
        lookupParams: {
          entity: 'Equipment',
          defaultField: 'name',
          dataTestId: 'equipments-field'
        }
      },
      {
        type: 'tag',
        tabIndex: tabIndexMapper.TAG,
        tabTitle: getResourceTo('Route.AssingTag.label.tag'),
        testid: 'routeAssignments-tags',
        title: getResourceTo('tagAssignments'),
        lookupLabel: getResourceTo('equipment.tag'),
        lookupParams: {
          entity: 'Tag',
          defaultField: 'description',
          dataTestId: 'tags-field'
        }
      }
    ];
  }, []);

  const handleChangeOption = (event) => {
    setSelectedOption(Number(event.target.value));
  };

  useEffect(() => {
    const { assignments } = allAssignments.equipment;

    const equipments = assignments.map(({ equipment, principal }) => ({
      ...equipment,
      principal
    }));

    const equipmentPrincipal = equipments.find(
      (equipment) => equipment.principal
    );

    if (equipmentPrincipal) {
      setSelectedOption(equipmentPrincipal.id);
    }
  }, [allAssignments]);

  const getCustomSelectedItem = (item, value) => (
    <CustomSelectedItem
      selectedOption={selectedOption}
      item={item}
      value={value}
      onChange={handleChangeOption}
    />
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  if (!isOpen) return null;

  return (
    <Modal
      className={`routeAssignments route-${routeId}`}
      isOpen={isOpen}
      title={title}
      subtitle={subtitle}
      crossAxisBtn={false}
      closeModalFn={closeModal}
      footer={
        <ModalFooter justifyContent="flex-end" gap={12}>
          <Button
            dataTestId="close-modal-button"
            variant="secondary"
            onClick={closeModal}
          >
            {getResourceTo('default.button.cancel')}
          </Button>
          <Button
            onClick={() => formRefs.current[activeIndexTab].submit()}
            dataTestId="save-button"
            variant="primary"
            loading={isUpdating}
          >
            {getResourceTo('default.button.save')}
          </Button>
        </ModalFooter>
      }
      shouldCloseOnOverlayClick
      onRequestClose={closeModal}
    >
      <div className="react-tabs-wrapper">
        <Tabs
          defaultIndex={activeIndexTab}
          onSelect={(tab) => setActiveIndexTab(Number(tab))}
        >
          <TabList>
            {tabsContentList.map((tab) => (
              <Tab
                key={tab.type}
                tabIndex={tab.tabIndex}
                data-testid={`tab-${tab.type}`}
              >
                {tab.tabTitle}
              </Tab>
            ))}
          </TabList>
          <LoadingContainer
            shouldShow={isFetching}
            alignItems="center"
            justifyContent="center"
          >
            <Loading />
          </LoadingContainer>
          {!isFetching &&
            tabsContentList.map((content) => (
              <TabPanel className={`${content.type}-tab`} key={content.type}>
                <Container
                  data-testid={content.testid}
                  flexDirection="column"
                  gap={10}
                  id={`form_route_assignment_${content.type}`}
                >
                  <Text type="primary" dataTestId="route-assignment-form-title">
                    {content.title}
                  </Text>
                  <Form
                    ref={(el) => {
                      formRefs.current[
                        tabIndexMapper[content.type.toUpperCase()]
                      ] = el;
                    }}
                    onSubmit={(data) => handleSubmit(data, content.type)}
                  >
                    <LookupMultiSelect
                      label={content.lookupLabel}
                      name={content.type}
                      displayMode="form"
                      defaultValue={allAssignments[content.type].list}
                      onSelectValues={(items) => setSelectedOption(items[0].id)}
                      customSelectedItem={
                        content.type === 'equipment'
                          ? getCustomSelectedItem
                          : undefined
                      }
                      {...getLookupExtraParams({
                        ...content.lookupParams,
                        autoComplete: 'off'
                      })}
                    />
                  </Form>
                </Container>
              </TabPanel>
            ))}
        </Tabs>
      </div>
    </Modal>
  );
}

export default RouteAssignmentsModal;
